import { useState } from 'react';
import { X, Delete, Check } from 'lucide-react';

export function NumpadDecimal({ value, onChange, onConfirm, onCancel, title, subtitle, maxValue = 9999 }) {
  const [display, setDisplay] = useState(value?.toString() || '0');

  const handleKey = (key) => {
    if (key === 'clear') {
      setDisplay('0');
    } else if (key === 'backspace') {
      const newVal = display.length > 1 ? display.slice(0, -1) : '0';
      setDisplay(newVal);
    } else if (key === '.') {
      // Only add decimal if not already present
      if (!display.includes('.')) {
        setDisplay(display + '.');
      }
    } else {
      // Limit decimal places to 2
      const parts = display.split('.');
      if (parts.length === 2 && parts[1].length >= 2) {
        return; // Already has 2 decimal places
      }
      
      const newVal = display === '0' && key !== '.' ? key : display + key;
      const numVal = parseFloat(newVal) || 0;
      if (numVal <= maxValue) {
        setDisplay(newVal);
      }
    }
  };

  const handleConfirm = () => {
    const numVal = parseFloat(display) || 0;
    onConfirm?.(numVal);
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
      onClick={onCancel}
      data-testid="numpad-overlay"
    >
      <div 
        className="bg-white w-full rounded-t-3xl p-6 pb-8 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="numpad-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            {title && <h2 className="text-xl font-bold text-zinc-900">{title}</h2>}
            {subtitle && <p className="text-zinc-500">{subtitle}</p>}
          </div>
          <button 
            onClick={onCancel}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-zinc-100"
            data-testid="numpad-cancel"
          >
            <X className="w-5 h-5 text-zinc-600" />
          </button>
        </div>

        {/* Display */}
        <div className="bg-zinc-100 rounded-xl px-4 py-3 mb-4 text-center">
          <span className="text-4xl font-bold text-zinc-900" data-testid="numpad-display">
            {display}
          </span>
          <span className="text-xl text-zinc-500 ml-2">kg</span>
        </div>

        {/* Numpad Grid */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
            <button
              key={num}
              className="h-16 bg-zinc-100 rounded-xl text-2xl font-bold text-zinc-900 active:bg-zinc-200"
              onClick={() => handleKey(num)}
              data-testid={`numpad-key-${num}`}
            >
              {num}
            </button>
          ))}
          <button
            className="h-16 bg-zinc-100 rounded-xl text-2xl font-bold text-zinc-900 active:bg-zinc-200"
            onClick={() => handleKey('.')}
            data-testid="numpad-key-dot"
          >
            .
          </button>
          <button
            className="h-16 bg-zinc-100 rounded-xl text-2xl font-bold text-zinc-900 active:bg-zinc-200"
            onClick={() => handleKey('0')}
            data-testid="numpad-key-0"
          >
            0
          </button>
          <button
            className="h-16 bg-zinc-100 rounded-xl flex items-center justify-center active:bg-zinc-200"
            onClick={() => handleKey('backspace')}
            data-testid="numpad-key-backspace"
          >
            <Delete className="w-6 h-6 text-zinc-600" />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleKey('clear')}
            className="h-14 bg-zinc-100 text-zinc-900 rounded-xl text-lg font-medium"
            data-testid="numpad-btn-clear"
          >
            Limpiar
          </button>
          <button
            onClick={handleConfirm}
            className="h-14 bg-zinc-900 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2"
            data-testid="numpad-btn-confirm"
          >
            <Check className="w-5 h-5" />
            Confirmar
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
